import React, { useMemo } from "react";
import { NeoModal, NeoCard, NeoBadge } from "../../../../components/Common";

/**
 * ClassPlanModal
 * Modal compacto de Planejamento de Aula.
 */
const ClassPlanModal = ({ isOpen, onClose, session }) => {
    const plan = session?.lessonPlan;

    const objectivesList = useMemo(() => {
        if (!plan?.objectives) return [];
        return plan.objectives;
    }, [plan]);

    if (!session) return null;

    const activityName = session.activityName || "Atividade";
    const sessionDate = session.sessionDate || "";
    const startTime = session.startTime || "";
    const endTime = session.endTime || "";
    const employeeName = session.employeeName || "";

    const dateFormatted = sessionDate
        ? new Date(sessionDate + "T12:00:00").toLocaleDateString("pt-BR", {
            weekday: "long",
            day: "2-digit",
            month: "long"
        })
        : "";

    const getScoreColor = (avgScore, maxLevel = 4) => {
        const pct = maxLevel > 0 ? (avgScore / maxLevel) * 100 : 0;
        if (pct >= 75) return "success";
        if (pct >= 50) return "info";
        if (pct >= 25) return "warning";
        return "danger";
    };

    return (
        <NeoModal
            isOpen={isOpen}
            toggle={onClose}
            title={activityName}
            subtitle={`${dateFormatted} • ${startTime}–${endTime}${employeeName ? ` • ${employeeName}` : ""}`}
            headerIcon="mdi-clipboard-text-outline"
            size="lg"
            centered
            scrollable
        >
            {/* Info inline */}
            {plan && (
                <div className="d-flex align-items-center justify-content-between mb-3 px-1 flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-4">
                        <span className="font-size-12 text-muted d-flex align-items-center">
                            <i className="mdi mdi-account-group me-1 text-primary font-size-15"></i>
                            <span><strong>{plan.classMemberCount || 0}</strong> alunos</span>
                        </span>
                        <span className="font-size-12 text-muted d-flex align-items-center">
                            <i className="mdi mdi-chart-line me-1 text-primary font-size-15"></i>
                            <span>Média: <strong className={`text-${getScoreColor(plan.classAvgOverall)}`}>{plan.classAvgOverall?.toFixed(1) || "0.0"}</strong></span>
                        </span>
                        <span className="font-size-12 text-muted d-flex align-items-center">
                            <i className="mdi mdi-target me-1 text-primary font-size-15"></i>
                            <span><strong>{objectivesList.length}</strong> objetivo(s)</span>
                        </span>
                    </div>
                    <NeoBadge color="primary" className="font-size-10 px-2 py-1 flex-shrink-0">
                        Ciclo {plan.cyclePosition}/{plan.cycleLength}
                    </NeoBadge>
                </div>
            )}

            {/* Sem plano */}
            {!plan && (
                <div className="text-center py-5">
                    <div className="avatar-lg mx-auto mb-3">
                        <div className="avatar-title bg-primary-subtle text-primary rounded-circle display-4">
                            <i className="mdi mdi-clipboard-text-outline"></i>
                        </div>
                    </div>
                    <h5 className="fw-bold">Nenhum planejamento gerado</h5>
                    <p className="text-muted font-size-13 mb-0">
                        Clique em <strong>"Gerar Planejamento"</strong> para criar o plano desta turma.
                    </p>
                </div>
            )}

            {/* Objetivos */}
            {plan && objectivesList.length > 0 && (
                <div className="d-flex flex-column gap-3">
                    {objectivesList.map((objective, idx) => (
                        <NeoCard key={objective.id || idx} thinBorder className="shadow-none mb-0" bodyClassName="p-3">
                            {/* Header do Objetivo */}
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <div className="d-flex align-items-center gap-2">
                                    <i className="mdi mdi-bullseye-arrow text-primary font-size-16"></i>
                                    <span className="fw-bold font-size-14 text-dark">{objective.name}</span>
                                    <NeoBadge
                                        color={
                                            objective.reason === "fundamental" ? "warning"
                                                : objective.reason === "priority" ? "danger"
                                                    : "secondary"
                                        }
                                        className="font-size-9"
                                    >
                                        {objective.reason === "fundamental" ? "Fundamental"
                                            : objective.reason === "priority" ? "Prioridade"
                                                : "Regular"}
                                    </NeoBadge>
                                </div>
                                <span className={`fw-bold font-size-16 text-${getScoreColor(objective.avgScore)}`}>
                                    {objective.avgScore?.toFixed(1) || "0.0"}
                                </span>
                            </div>

                            {/* Barra de progresso circular-capsular do Objetivo */}
                            <div className="progress mb-3 animate__animated animate__fadeIn" style={{ height: "6px", borderRadius: "10px" }}>
                                <div
                                    className={`progress-bar bg-${getScoreColor(objective.avgScore)} rounded-pill`}
                                    role="progressbar"
                                    style={{ width: `${(objective.avgScore || 0) / 4 * 100}%` }}
                                    aria-valuenow={objective.avgScore}
                                    aria-valuemin="0"
                                    aria-valuemax="4"
                                />
                            </div>

                            {/* Tópicos compactos */}
                            {objective.topics && objective.topics.length > 0 && (
                                <div className="d-flex flex-column gap-1.5 mt-2">
                                    {objective.topics.map((topic, tIdx) => (
                                        <div
                                            key={topic.id || tIdx}
                                            className="d-flex align-items-center justify-content-between py-1.5 px-3 rounded bg-light border border-light"
                                            style={{ fontSize: "12px" }}
                                        >
                                            <div className="d-flex align-items-center gap-1.5 text-muted">
                                                {topic.isFundamental ? (
                                                    <i className="mdi mdi-star text-warning font-size-12"></i>
                                                ) : (
                                                    <i className="mdi mdi-circle-small text-muted font-size-14"></i>
                                                )}
                                                <span className="text-dark fw-medium">{topic.name}</span>
                                            </div>
                                            <div className="d-flex align-items-center gap-2" style={{ minWidth: "120px" }}>
                                                <div className="progress flex-grow-1 animate__animated animate__fadeIn" style={{ height: "4px", borderRadius: "10px" }}>
                                                    <div
                                                        className={`progress-bar bg-${getScoreColor(topic.avg)} rounded-pill`}
                                                        role="progressbar"
                                                        style={{ width: `${(topic.avg || 0) / 4 * 100}%` }}
                                                        aria-valuenow={topic.avg}
                                                        aria-valuemin="0"
                                                        aria-valuemax="4"
                                                    />
                                                </div>
                                                <span className={`fw-bold text-${getScoreColor(topic.avg)}`} style={{ minWidth: "24px", textAlign: "right", fontSize: "11px" }}>
                                                    {topic.avg?.toFixed(1) || "0.0"}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </NeoCard>
                    ))}
                </div>
            )}
        </NeoModal>
    );
};

export default ClassPlanModal;
